import React from 'react';
import { Box, Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import type { ManualOnboardingField, OnboardingChecklistItem } from '../../../../types/features/storeDetails/onboarding';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import { adminCheckboxSx, adminCombineSx } from '../../../../styles/adminComponentStyles';

type StoreOnboardingChecklistGroupProps = {
    title: string;
    items: OnboardingChecklistItem[];
    isSavingOnboardingField: boolean;
    onUpdateOnboardingField: (field: ManualOnboardingField, value: boolean) => void;
    withDividerTop?: boolean;
};

const StoreOnboardingChecklistGroup: React.FC<StoreOnboardingChecklistGroupProps> = ({
    title,
    items,
    isSavingOnboardingField,
    onUpdateOnboardingField,
    withDividerTop = false,
}) => (
    <Box sx={withDividerTop ? { borderTop: `1px solid ${adminColors.divider}`, mt: 1.5, pt: 1.5 } : undefined}>
        {title ? (
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mt: withDividerTop ? 0 : 0.75, mb: 0.5 }}>
                {title}
            </Typography>
        ) : null}
        {items.map((item) => (
            <Stack key={item.label} direction="row" alignItems="flex-start" spacing={0.5} sx={{ py: 0.25 }}>
                <Checkbox
                    checked={item.value}
                    disabled={!item.editable || isSavingOnboardingField}
                    onChange={(event) => item.editable && item.field && onUpdateOnboardingField(item.field, event.target.checked)}
                    size="small"
                    sx={adminCombineSx(adminCheckboxSx, { p: 0.25, mt: 0.125, width: 22 })}
                />
                <Box sx={{ flex: 1 }}>
                    <Tooltip title={item.help} placement="right">
                        <Typography sx={{ ...adminTypography.body, color: item.value ? adminColors.subtleText : adminColors.text, fontSize: '14px', lineHeight: 1.35, textDecoration: item.value ? 'line-through' : 'none', flex: 1 }}>
                            {item.label}
                        </Typography>
                    </Tooltip>
                    {item.detail ? (
                        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                            {item.detail}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        ))}
    </Box>
);

export default React.memo(StoreOnboardingChecklistGroup);
